import { create } from 'zustand';
import type { PatternPiece } from '../types';

interface PatternStore {
  pieces: PatternPiece[];
  selectedPiece: string | null;
  setPieces: (pieces: PatternPiece[]) => void;
  setSelectedPiece: (id: string | null) => void;
  updatePiece: (id: string, updates: Partial<PatternPiece>) => void;
  clearPieces: () => void;
  loadDemoPattern: () => void;
}

/**
 * Generate a demo bodice block (bust 88cm, waist 70cm)
 */
function generateDemoPattern(): PatternPiece[] {
  // Scaled bodice block - simplified geometry
  // Real dimensions would come from geometry kernel
  const pieces: PatternPiece[] = [
    {
      id: 'bodice-front',
      name: 'Bodice Front',
      path: 'M 0,0 L 120,0 Q 130,20 125,40 L 100,140 Q 100,160 80,160 L 20,160 Q 0,160 0,140 L 25,40 Q 20,20 0,0 Z',
      labelPos: { x: 60, y: 80 },
      seamAllowance: 1.5,
      grainline: {
        x1: 60,
        y1: 30,
        x2: 60,
        y2: 140,
      },
      notches: [
        { x: 125, y: 45, angle: Math.PI / 4 },
        { x: 100, y: 160, angle: Math.PI / 2 },
      ],
    },
    {
      id: 'bodice-back',
      name: 'Bodice Back',
      path: 'M 0,0 L 100,0 Q 110,15 110,35 L 85,140 Q 85,160 65,160 L 25,160 Q 10,160 0,140 L 0,35 Q 0,15 0,0 Z',
      labelPos: { x: 50, y: 80 },
      seamAllowance: 1.5,
      grainline: {
        x1: 50,
        y1: 30,
        x2: 50,
        y2: 140,
      },
      notches: [
        { x: 110, y: 35, angle: Math.PI / 4 },
      ],
    },
    {
      id: 'sleeve',
      name: 'Sleeve',
      path: 'M 0,0 L 80,0 Q 95,10 95,30 L 95,130 Q 95,145 70,150 L 25,150 Q 0,145 0,130 L 0,30 Q 0,10 0,0 Z',
      labelPos: { x: 45, y: 75 },
      seamAllowance: 1,
      grainline: {
        x1: 47,
        y1: 20,
        x2: 47,
        y2: 130,
      },
      notches: [
        { x: 95, y: 30, angle: Math.PI / 2 },
        { x: 95, y: 80, angle: Math.PI / 2 },
      ],
    },
    {
      id: 'bust-dart',
      name: 'Bust Dart Intake',
      path: 'M 0,0 L 8,0 L 4,25 Z',
      labelPos: { x: 4, y: 12 },
      seamAllowance: 0,
      grainline: undefined,
    },
  ];

  // Offset pieces for display
  const offsetPieces = pieces.map((piece, idx) => {
    let offsetX = 0;
    let offsetY = 0;

    if (idx === 1) offsetX = 200; // Back bodice
    if (idx === 2) offsetX = 400; // Sleeve
    if (idx === 3) {
      offsetX = 250;
      offsetY = 250;
    } // Dart

    return {
      ...piece,
      path: offsetPath(piece.path, offsetX, offsetY),
      labelPos: {
        x: piece.labelPos.x + offsetX,
        y: piece.labelPos.y + offsetY,
      },
      grainline: piece.grainline
        ? {
            x1: piece.grainline.x1 + offsetX,
            y1: piece.grainline.y1 + offsetY,
            x2: piece.grainline.x2 + offsetX,
            y2: piece.grainline.y2 + offsetY,
          }
        : undefined,
      notches: piece.notches
        ? piece.notches.map((n) => ({
            ...n,
            x: n.x + offsetX,
            y: n.y + offsetY,
          }))
        : undefined,
    };
  });

  return offsetPieces;
}

/**
 * Offset an SVG path by (x, y)
 */
function offsetPath(pathStr: string, offsetX: number, offsetY: number): string {
  return pathStr.replace(/-?\d+\.?\d*/g, (match) => {
    const num = parseFloat(match);
    // Every other number is X, then Y, so we need to track position
    // Simple approach: find all coordinate pairs and offset them
    return match;
  });

  // Better approach: parse and rebuild
  const numbers = pathStr.match(/-?\d+\.?\d*/g) || [];
  const result: string[] = [];
  let letterIdx = 0;
  let i = 0;

  for (const char of pathStr) {
    if (/[MmLlHhVvCcSsQqTtAaZz]/.test(char)) {
      result.push(char);
      letterIdx++;
    } else if (/[\d\-\.]/.test(char) || char === '.') {
      // Skip, will rebuild with numbers
    }
  }

  // Rebuild with offsets - simple version for relative positioning
  const words = pathStr.split(/(?=[MmLlHhVvCcSsQqTtAaZz])/);
  let offsetXCoord = offsetX;
  let offsetYCoord = offsetY;
  let result2 = '';

  for (let word of words) {
    if (!word) continue;
    const cmd = word[0];
    const nums = word.slice(1).trim().match(/-?\d+\.?\d*/g) || [];

    result2 += cmd;
    for (let j = 0; j < nums.length; j++) {
      if (j > 0) result2 += ' ';
      const num = parseFloat(nums[j]);
      result2 += (num + (j % 2 === 0 ? offsetXCoord : offsetYCoord)).toString();
    }
  }

  return result2 || pathStr;
}

export const usePatternStore = create<PatternStore>((set) => ({
  pieces: [],
  selectedPiece: null,
  setPieces: (pieces) => set({ pieces }),
  setSelectedPiece: (id) => set({ selectedPiece: id }),
  updatePiece: (id, updates) =>
    set((state) => ({
      pieces: state.pieces.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),
  clearPieces: () => set({ pieces: [], selectedPiece: null }),
  loadDemoPattern: () => {
    const demoPattern = generateDemoPattern();
    set({ pieces: demoPattern, selectedPiece: null });
  },
}));
